'use strict';

const libPictProvider = require('pict-provider');
const libAssetDescriptor = require('../assets/AssetDescriptor.js');

const DETAIL_MOUNT = '#ATD-Detail';
const BODY_MOUNT = '#ATD-Body';
const STATUS_MOUNT = '#ATD-Status';

// ManifestJSON is MEDIUMTEXT once migrate-once/WidenAssetTypeManifestJSON.sql has run, and plain TEXT
// before that. A non-strict MySQL TRUNCATES an oversized value rather than refusing it, which leaves
// unparseable JSON that normalizeDescriptor swallows into {} — a blank screen with no error anywhere.
// So the editor refuses first, and warns well before the older limit in case the migration is behind.
const MEDIUMTEXT_BYTES = 16777215;
const TEXT_BYTES = 65535;

/**
 * AssetTypeDescriptor — the bespoke #/AssetTypeDescriptor screen: the metadata SHAPE editor for an
 * asset type. It owns the v2 descriptor inside AssetTypeManifest.ManifestJSON — which fields the
 * asset detail panel shows, what they are called, their units and help, which are map metrics, and
 * the order they read in.
 *
 * Three rules this screen exists to respect, each of which has already bitten someone:
 *
 *  1. ManifestJSON is a SHARED blob. The legacy flat `Properties` array (#/AssetTypeManifests) lives
 *     in the same column, and normalizeDescriptor does not round-trip it. So the raw parsed blob is
 *     kept on state and the edited descriptor is overlaid onto it — never the other way round.
 *  2. `Status` is the on switch and its absence is off. A descriptor without Status:'published' is
 *     skipped by the resolver, and the caller silently gets the compiled-in SNBI (bridge) vocabulary
 *     instead — so a culvert would render as a bridge. Every write here stamps Status explicitly.
 *  3. Field `Code` and metadata-set `Key` are PRIMARY KEYS for stored data: PhysicalAssetMetadata
 *     rows bind by (MetadataSetGUID = set key, Address = field code). Renaming either orphans every
 *     stored value with no error and no migration. They are read-only here; you rename the Label.
 *
 * Saving never mutates a published row in place. A published row is serving every asset of the type
 * right now, so an edit to one becomes a NEW version; only a draft row is written over.
 */
class AssetTypeDescriptorProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		// Monotonic counter behind the provisional identifiers authored nodes carry until their first save.
		this._newSequence = 0;
		// Embed mode (hosted inside the AssetType workspace's Metadata Shape tab): lock to one type, hide its picker.
		this._fixedIDAssetType = 0;
		this._embedHost = '';
	}

	get restClient() { return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient; }

	get state()
	{
		if (!this.pict.AppData.AssetTypeDescriptor)
		{
			this.pict.AppData.AssetTypeDescriptor =
			{
				Loaded: false, Loading: false, Saving: false, Error: '', Message: '',
				Types: [], IDAssetType: 0,
				// The raw parsed blob exactly as stored. Everything this screen does not understand
				// rides along in here and is written back untouched.
				Raw: null,
				Descriptor: null,
				Row: null, Versions: [],
				SetKey: '', Filter: '', Expanded: {}, Dirty: false,
			};
		}
		return this.pict.AppData.AssetTypeDescriptor;
	}

	/** UI-only hint; the server stays authoritative. Fails open like the rest of the app. */
	canEdit()
	{
		const tmpService = this.pict.fable.EffectivePermissionsService || (this.fable && this.fable.EffectivePermissionsService);
		if (!tmpService || (typeof tmpService.can !== 'function')) { return true; }
		return tmpService.can('AssetTypeManifest', 'Update') || tmpService.can('AssetTypeManifest', 'Create');
	}

	// --- embed API (the AssetType workspace's Metadata Shape tab) ---
	// Lock the editor to one IDAssetType, hide the type picker, and paint into the tab host. Idempotent:
	// the workspace re-creates the host div and calls this on every paint of the active embed tab.
	openInto(pIDAssetType, pHostSelector)
	{
		const tmpID = Number(pIDAssetType) || 0;
		this._embedHost = pHostSelector || '';
		const tmpChanged = (Number(this._fixedIDAssetType) !== tmpID);
		this._fixedIDAssetType = tmpID;
		try { if (this.pict.CSSMap && (typeof this.pict.CSSMap.injectCSS === 'function')) { this.pict.CSSMap.injectCSS(); } } catch (pError) { /* best-effort */ }
		// Re-paint the detail scaffold into the (re-created) host so render()'s #ATD-Detail mount exists here.
		if (pHostSelector) { this.pict.ContentAssignment.assignContent(pHostSelector, '<div class="atd-embed"><div id="ATD-Detail" class="atd-main"></div></div>'); }
		const tmpState = this.state;
		tmpState.IDAssetType = tmpID; // so loadAsync's remembered-type logic selects THIS type
		if (tmpChanged || !tmpState.Loaded) { this.loadAsync(); }
		else { this.render(); }
	}
	exitEmbed() { this._fixedIDAssetType = 0; this._embedHost = ''; }

	_get(pURI) { return new Promise((resolve, reject) => this.restClient.getJSON(pURI, (pError, pBody) => (pError ? reject(pError) : resolve(pBody)))); }
	_post(pURI, pBody) { return new Promise((resolve, reject) => this.restClient.postJSON(pURI, pBody, (pError, pResult) => (pError ? reject(pError) : resolve(pResult)))); }
	_put(pURI, pBody) { return new Promise((resolve, reject) => this.restClient.putJSON(pURI, pBody, (pError, pResult) => (pError ? reject(pError) : resolve(pResult)))); }

	async _getAllPages(pURI, pPage)
	{
		const tmpPage = pPage || 250;
		const tmpOut = [];
		let tmpOffset = 0;
		for (;;)
		{
			const tmpRows = await this._get(`${ pURI }/${ tmpOffset }/${ tmpPage }`);
			if (!Array.isArray(tmpRows) || (tmpRows.length === 0)) { break; }
			tmpOut.push(...tmpRows);
			if (tmpRows.length < tmpPage) { break; }
			tmpOffset += tmpPage;
			if (tmpOffset > 100000) { break; }
		}
		return tmpOut;
	}

	_esc(pString) { return String(pString == null ? '' : pString).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar])); }
	/** Escape for an inline handler's single-quoted JS string argument inside a DOUBLE-quoted HTML attribute:
	 * JS-escape backslash + single quote, and HTML-escape & and " so a typed identifier cannot break out of
	 * the attribute (field codes / set keys / value codes are free-text here). & first so later entities survive. */
	_js(pString) { return String(pString == null ? '' : pString).replace(/&/g, '&amp;').replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/"/g, '&quot;'); }

	// A recognizable, unique placeholder identifier for a node the user just created. It is a non-empty
	// string (so normalizeDescriptor will not silently drop the node) that the save guard refuses to
	// store, forcing the author to give the node a real key before it can be written.
	_provisional(pKind) { this._newSequence += 1; return `__new_${ pKind }_${ this._newSequence }`; }
	_isProvisional(pString) { return /^__new_(set|group|field|value)_\d+$/.test(String(pString == null ? '' : pString)); }
	// A node's Label defaults to its Code/Key, so a brand-new node's label mirrors its provisional id; show
	// blank (placeholder) instead until the author types a real one.
	_cleanLabel(pNode) { return (pNode && pNode._New && this._isProvisional(pNode.Label)) ? '' : ((pNode && pNode.Label) || ''); }

	/** Object-or-string, and never throw: a truncated blob must not take the screen down. */
	_parseManifest(pRaw)
	{
		if (pRaw && (typeof pRaw === 'string')) { try { return JSON.parse(pRaw) || {}; } catch (pError) { return {}; } }
		if (pRaw && (typeof pRaw === 'object')) { return pRaw; }
		return {};
	}

	// ============================================================ load ============================================================

	async loadAsync()
	{
		const tmpState = this.state;
		tmpState.Loading = true; tmpState.Error = ''; tmpState.Message = '';
		this.render();
		try
		{
			tmpState.Types = await this._getAllPages('AssetTypes/FilteredTo/FBV~Deleted~EQ~0');
			tmpState.Loaded = true;
			if (tmpState.Types.length)
			{
				const tmpRemembered = tmpState.Types.find((pType) => (Number(pType.IDAssetType) === Number(tmpState.IDAssetType)));
				await this.selectTypeAsync((tmpRemembered || tmpState.Types[0]).IDAssetType);
			}
		}
		catch (pError)
		{
			tmpState.Error = `Could not load the asset types: ${ (pError && pError.message) || pError }`;
		}
		finally
		{
			tmpState.Loading = false;
			this.render();
		}
	}

	/**
	 * Load the newest manifest row for a type — newest overall, not newest published, so a draft in
	 * progress is what you come back to.
	 *
	 * @param {Number|String} pIDAssetType
	 */
	async selectTypeAsync(pIDAssetType)
	{
		const tmpState = this.state;
		const tmpID = Number(pIDAssetType) || 0;
		if (!tmpID) { return; }
		tmpState.IDAssetType = tmpID;
		tmpState.Error = ''; tmpState.Message = '';
		tmpState.Raw = null; tmpState.Descriptor = null; tmpState.Row = null; tmpState.Versions = [];
		tmpState.SetKey = ''; tmpState.Expanded = {}; tmpState.Dirty = false; tmpState.Filter = '';
		tmpState.Loading = true;
		this.render();
		try
		{
			tmpState.Versions = await this._getAllPages(`AssetTypeManifests/FilteredTo/FBV~IDAssetType~EQ~${ tmpID }~FBV~Deleted~EQ~0`);
			const tmpNewest = tmpState.Versions.reduce((pBest, pRow) => ((!pBest || (Number(pRow.Version) > Number(pBest.Version))) ? pRow : pBest), null);
			if (tmpNewest)
			{
				// Re-read the single row: the Reads route does not always carry the blob, and this is
				// also the freshest copy immediately before an edit.
				const tmpRow = await this._get(`AssetTypeManifest/${ Number(tmpNewest.IDAssetTypeManifest) }`);
				const tmpBlob = (tmpRow && (tmpRow.Manifest != null)) ? tmpRow.Manifest : (tmpRow ? tmpRow.ManifestJSON : null);
				tmpState.Row = { IDAssetTypeManifest: Number(tmpNewest.IDAssetTypeManifest), Version: Number(tmpNewest.Version) };
				tmpState.Raw = this._parseManifest(tmpBlob);
			}
			else
			{
				tmpState.Row = null;
				tmpState.Raw = {};
			}
			tmpState.Descriptor = libAssetDescriptor.normalizeDescriptor(tmpState.Raw);
			tmpState.SetKey = tmpState.Descriptor.Dashboard.DefaultSet
				|| (tmpState.Descriptor.MetadataSets.length ? tmpState.Descriptor.MetadataSets[0].Key : '');
		}
		catch (pError)
		{
			tmpState.Error = `Could not load the metadata shape: ${ (pError && pError.message) || pError }`;
		}
		finally
		{
			tmpState.Loading = false;
			this.render();
		}
	}

	// ======================================================== editing ========================================================

	get accessor()
	{
		return new libAssetDescriptor.AssetDescriptorAccessor(this.state.Descriptor, { SetKey: this.state.SetKey });
	}

	get set()
	{
		const tmpState = this.state;
		if (!tmpState.Descriptor) { return null; }
		return tmpState.Descriptor.MetadataSets.find((pSet) => (pSet.Key === tmpState.SetKey)) || null;
	}

	_field(pCode)
	{
		const tmpSet = this.set;
		return tmpSet ? (tmpSet.Fields.find((pField) => (pField.Code === pCode)) || null) : null;
	}

	_touch() { this.state.Dirty = true; this.state.Message = ''; }

	setSetKey(pKey) { this.state.SetKey = pKey; this.state.Expanded = {}; this.render(); }
	setSetLabel(pKey, pValue) { const tmpD = this.state.Descriptor; if (!tmpD) { return; } const tmpSet = tmpD.MetadataSets.find((pSet) => (pSet.Key === pKey)); if (tmpSet) { tmpSet.Label = String(pValue || ''); this._touch(); this._syncStatus(); } }
	setFilter(pValue) { this.state.Filter = String(pValue || ''); this._repaintBody(); }
	toggleExpand(pCode) { const tmpState = this.state; tmpState.Expanded[pCode] = !tmpState.Expanded[pCode]; this._repaintBody(); }

	toggleVisible(pCode)
	{
		const tmpField = this._field(pCode);
		if (!tmpField) { return; }
		tmpField.Visible = !tmpField.Visible;
		this._touch(); this._repaintBody();
	}

	toggleMetric(pCode)
	{
		const tmpField = this._field(pCode);
		if (!tmpField) { return; }
		tmpField.Metric = !tmpField.Metric;
		// Filterable has always defaulted to Metric; keep the pair consistent so the stored descriptor
		// does not develop a contradiction the editor never showed anyone.
		tmpField.Filterable = tmpField.Metric;
		// A hidden field cannot be a metric — metrics() excludes it, so leaving the flag on would be a
		// switch that visibly does nothing.
		if (tmpField.Metric) { tmpField.Visible = true; }
		this._touch(); this._repaintBody();
	}

	// Text setters do NOT repaint: the caret is in the input the user is typing into.
	setLabel(pCode, pValue) { const tmpField = this._field(pCode); if (tmpField) { tmpField.Label = String(pValue || ''); this._touch(); this._syncStatus(); } }
	setUnits(pCode, pValue) { const tmpField = this._field(pCode); if (tmpField) { tmpField.Units = String(pValue || '').trim() || null; this._touch(); this._syncStatus(); } }
	setHelp(pCode, pValue) { const tmpField = this._field(pCode); if (tmpField) { tmpField.Help = String(pValue || ''); this._touch(); this._syncStatus(); } }
	setGroupLabel(pKey, pValue)
	{
		const tmpSet = this.set;
		if (!tmpSet) { return; }
		const tmpGroup = tmpSet.Groups.find((pGroup) => (pGroup.Key === pKey));
		if (tmpGroup) { tmpGroup.Label = String(pValue || ''); this._touch(); this._syncStatus(); }
	}
	setAssetLabel(pWhich, pValue)
	{
		const tmpState = this.state;
		if (!tmpState.Descriptor) { return; }
		tmpState.Descriptor.AssetType.Label[pWhich] = String(pValue || '');
		this._touch(); this._syncStatus();
	}
	setValueLabel(pCode, pValue, pLabel)
	{
		const tmpField = this._field(pCode);
		if (!tmpField) { return; }
		const tmpEntry = tmpField.Values.find((pItem) => (pItem.Value === pValue));
		if (tmpEntry) { tmpEntry.Label = String(pLabel || ''); this._touch(); this._syncStatus(); }
	}

	setValueDisplay(pCode, pValue) { const tmpField = this._field(pCode); if (tmpField) { tmpField.Format.ValueDisplay = pValue; this._touch(); this._repaintBody(); } }
	setDecimals(pCode, pValue)
	{
		const tmpField = this._field(pCode);
		if (!tmpField) { return; }
		const tmpNumber = (String(pValue).trim() === '') ? null : Number(pValue);
		tmpField.Format.Decimals = Number.isFinite(tmpNumber) ? tmpNumber : null;
		this._touch(); this._syncStatus();
	}

	setDefaultMetric(pCode)
	{
		const tmpState = this.state;
		if (!tmpState.Descriptor) { return; }
		tmpState.Descriptor.Dashboard.DefaultMetric = pCode || null;
		this._touch(); this._syncStatus();
	}

	/** Show or hide every field in one group at once — the common case on a 149-field descriptor. */
	setGroupVisible(pKey, pVisible)
	{
		const tmpSet = this.set;
		if (!tmpSet) { return; }
		const tmpVisible = !!pVisible;
		tmpSet.Fields.forEach((pField) => { if ((pField.Group || '') === pKey) { pField.Visible = tmpVisible; if (!tmpVisible) { pField.Metric = false; pField.Filterable = false; } } });
		this._touch(); this._repaintBody();
	}

	/**
	 * Move a field within its group. Ordinals are renumbered densely afterwards so the stored order is
	 * unambiguous rather than depending on the array order that produced it.
	 *
	 * @param {String} pCode
	 * @param {Number} pDelta - -1 up, +1 down
	 */
	moveField(pCode, pDelta)
	{
		const tmpSet = this.set;
		const tmpField = this._field(pCode);
		if (!tmpSet || !tmpField) { return; }
		const tmpGroupKey = tmpField.Group || '';
		const tmpPeers = tmpSet.Fields.filter((pPeer) => ((pPeer.Group || '') === tmpGroupKey)).sort((pLeft, pRight) => (pLeft.Ordinal - pRight.Ordinal));
		const tmpIndex = tmpPeers.indexOf(tmpField);
		const tmpTarget = tmpIndex + Number(pDelta);
		if ((tmpIndex < 0) || (tmpTarget < 0) || (tmpTarget >= tmpPeers.length)) { return; }
		tmpPeers.splice(tmpIndex, 1);
		tmpPeers.splice(tmpTarget, 0, tmpField);
		tmpPeers.forEach((pPeer, pIndex) => { pPeer.Ordinal = pIndex; });
		this._touch(); this._repaintBody();
	}

	/** Move a whole group up or down, renumbering group ordinals densely. */
	moveGroup(pKey, pDelta)
	{
		const tmpSet = this.set;
		if (!tmpSet) { return; }
		const tmpGroups = tmpSet.Groups.slice().sort((pLeft, pRight) => (pLeft.Ordinal - pRight.Ordinal));
		const tmpGroup = tmpGroups.find((pItem) => (pItem.Key === pKey));
		const tmpIndex = tmpGroups.indexOf(tmpGroup);
		const tmpTarget = tmpIndex + Number(pDelta);
		if (!tmpGroup || (tmpTarget < 0) || (tmpTarget >= tmpGroups.length)) { return; }
		tmpGroups.splice(tmpIndex, 1);
		tmpGroups.splice(tmpTarget, 0, tmpGroup);
		tmpGroups.forEach((pItem, pIndex) => { pItem.Ordinal = pIndex * 10; });
		this._touch(); this._repaintBody();
	}

	// =================================================== structural authoring ===================================================
	// Add/remove/reorder of metadata sets, sections, fields and coded values. Everything mutates
	// state.Descriptor in place and rides the existing _touch/_mergedManifest/validate/save/publish
	// pipeline, so draft/publish/versioning/byte-guard are inherited. A node authored in-session carries
	// a transient `_New` flag — never serialized (normalize* rebuilds a fixed key set) and dropped when
	// selectTypeAsync reloads after save — so its Code/Key stays editable only until the first save.

	addMetadataSet()
	{
		const tmpState = this.state;
		if (!tmpState.Descriptor) { return; }
		const tmpKey = this._provisional('set');
		const tmpSet = libAssetDescriptor.normalizeSet({ Key: tmpKey, Label: '' }, tmpState.Descriptor.MetadataSets.length);
		tmpSet._New = true;
		// Seed one empty section so the author lands one click from adding a field.
		const tmpGroup = libAssetDescriptor.normalizeGroup({ Key: this._provisional('group'), Label: '' }, 0);
		tmpGroup._New = true;
		tmpSet.Groups.push(tmpGroup);
		tmpState.Descriptor.MetadataSets.push(tmpSet);
		tmpState.SetKey = tmpKey; tmpState.Expanded = {};
		this._touch(); this.render();
	}

	removeMetadataSet(pKey)
	{
		const tmpState = this.state;
		const tmpD = tmpState.Descriptor;
		if (!tmpD) { return; }
		const tmpSet = tmpD.MetadataSets.find((pSet) => (pSet.Key === pKey));
		if (!tmpSet) { return; }
		if (!tmpSet._New && (typeof window !== 'undefined') && !window.confirm(`Remove the "${ tmpSet.Label || tmpSet.Key }" metadata set and everything in it? Stored values are kept but will no longer be shown or decoded for any asset of this type.`)) { return; }
		tmpD.MetadataSets = tmpD.MetadataSets.filter((pSet) => (pSet.Key !== pKey));
		if (tmpState.SetKey === pKey) { tmpState.SetKey = tmpD.MetadataSets.length ? tmpD.MetadataSets[0].Key : ''; }
		if (tmpD.Dashboard.DefaultSet === pKey) { tmpD.Dashboard.DefaultSet = null; }
		tmpState.Expanded = {};
		this._touch(); this.render();
	}

	addGroup()
	{
		const tmpSet = this.set;
		if (!tmpSet) { return; }
		const tmpGroup = libAssetDescriptor.normalizeGroup({ Key: this._provisional('group'), Label: '' }, tmpSet.Groups.length);
		tmpGroup._New = true;
		tmpGroup.Ordinal = tmpSet.Groups.reduce((pMax, pItem) => Math.max(pMax, pItem.Ordinal), -10) + 10;
		tmpSet.Groups.push(tmpGroup);
		this._touch(); this._repaintBody();
	}

	removeGroup(pKey)
	{
		const tmpSet = this.set;
		if (!tmpSet) { return; }
		const tmpGroup = tmpSet.Groups.find((pItem) => (pItem.Key === pKey));
		if (!tmpGroup) { return; }
		// Removing a non-empty section would drop its fields into the synthesized "Other" bucket.
		if (tmpSet.Fields.some((pField) => ((pField.Group || '') === pKey)))
		{
			this.state.Error = 'Move or remove this section’s fields before removing the section.';
			this.render();
			return;
		}
		if (!tmpGroup._New && (typeof window !== 'undefined') && !window.confirm(`Remove the "${ tmpGroup.Label || pKey }" section?`)) { return; }
		tmpSet.Groups = tmpSet.Groups.filter((pItem) => (pItem.Key !== pKey));
		tmpSet.Groups.slice().sort((pLeft, pRight) => (pLeft.Ordinal - pRight.Ordinal)).forEach((pItem, pIndex) => { pItem.Ordinal = pIndex * 10; });
		this._touch(); this._repaintBody();
	}

	addField(pGroupKey)
	{
		const tmpSet = this.set;
		if (!tmpSet) { return; }
		const tmpCode = this._provisional('field');
		const tmpField = libAssetDescriptor.normalizeField({ Code: tmpCode, Label: '', Group: pGroupKey, DataType: 'string' }, tmpSet.Fields.length);
		tmpField._New = true;
		tmpField.Ordinal = tmpSet.Fields.filter((pField) => ((pField.Group || '') === pGroupKey)).reduce((pMax, pField) => Math.max(pMax, pField.Ordinal), -1) + 1;
		tmpSet.Fields.push(tmpField);
		this.state.Expanded[tmpCode] = true;
		this._touch(); this._repaintBody();
	}

	removeField(pCode)
	{
		const tmpSet = this.set;
		const tmpField = this._field(pCode);
		if (!tmpSet || !tmpField) { return; }
		if (!tmpField._New && (typeof window !== 'undefined') && !window.confirm(`Remove "${ tmpField.Label || pCode }"? This stops showing it on every asset of this type. Stored values are kept and reappear if you re-add the field with the same code.`)) { return; }
		const tmpGroupKey = tmpField.Group || '';
		tmpSet.Fields = tmpSet.Fields.filter((pField) => (pField !== tmpField));
		tmpSet.Fields.filter((pField) => ((pField.Group || '') === tmpGroupKey)).sort((pLeft, pRight) => (pLeft.Ordinal - pRight.Ordinal)).forEach((pField, pIndex) => { pField.Ordinal = pIndex; });
		delete this.state.Expanded[pCode];
		if (this.state.Descriptor.Dashboard.DefaultMetric === pCode) { this.state.Descriptor.Dashboard.DefaultMetric = null; }
		this._touch(); this._repaintBody();
	}

	addValue(pCode)
	{
		const tmpField = this._field(pCode);
		if (!tmpField) { return; }
		const tmpValue = libAssetDescriptor.normalizeValue({ Value: this._provisional('value'), Label: '' }, tmpField.Values.length);
		tmpValue._New = true;
		tmpValue.Ordinal = tmpField.Values.reduce((pMax, pItem) => Math.max(pMax, pItem.Ordinal), -1) + 1;
		tmpField.Values.push(tmpValue);
		// A value list IS the enum claim — keep the declared type honest.
		if (tmpField.DataType !== 'enum') { tmpField.DataType = 'enum'; }
		this._touch(); this._repaintBody();
	}

	removeValue(pCode, pValue)
	{
		const tmpField = this._field(pCode);
		if (!tmpField) { return; }
		tmpField.Values = tmpField.Values.filter((pItem) => (pItem.Value !== pValue));
		tmpField.Values.slice().sort((pLeft, pRight) => (pLeft.Ordinal - pRight.Ordinal)).forEach((pItem, pIndex) => { pItem.Ordinal = pIndex; });
		this._touch(); this._repaintBody();
	}

	moveValue(pCode, pValue, pDelta)
	{
		const tmpField = this._field(pCode);
		if (!tmpField) { return; }
		const tmpOrdered = tmpField.Values.slice().sort((pLeft, pRight) => (pLeft.Ordinal - pRight.Ordinal));
		const tmpIndex = tmpOrdered.findIndex((pItem) => (pItem.Value === pValue));
		const tmpTarget = tmpIndex + Number(pDelta);
		if ((tmpIndex < 0) || (tmpTarget < 0) || (tmpTarget >= tmpOrdered.length)) { return; }
		const tmpMoved = tmpOrdered.splice(tmpIndex, 1)[0];
		tmpOrdered.splice(tmpTarget, 0, tmpMoved);
		tmpOrdered.forEach((pItem, pIndex) => { pItem.Ordinal = pIndex; });
		this._touch(); this._repaintBody();
	}

	// --- identifier setters (effective only while the node is _New; onchange → repaint so handlers rebind) ---
	setNewSetKey(pOldKey, pValue)
	{
		const tmpState = this.state;
		const tmpD = tmpState.Descriptor;
		if (!tmpD) { return; }
		const tmpSet = tmpD.MetadataSets.find((pSet) => (pSet.Key === pOldKey));
		if (!tmpSet || !tmpSet._New) { return; }
		const tmpNew = String(pValue == null ? '' : pValue).trim();
		tmpSet.Key = tmpNew;
		if (tmpState.SetKey === pOldKey) { tmpState.SetKey = tmpNew; }
		if (tmpD.Dashboard.DefaultSet === pOldKey) { tmpD.Dashboard.DefaultSet = tmpNew; }
		this._touch(); this.render();
	}

	setNewGroupKey(pOldKey, pValue)
	{
		const tmpSet = this.set;
		if (!tmpSet) { return; }
		const tmpGroup = tmpSet.Groups.find((pItem) => (pItem.Key === pOldKey));
		if (!tmpGroup || !tmpGroup._New) { return; }
		const tmpNew = String(pValue == null ? '' : pValue).trim();
		// Re-home any fields already placed under the provisional key.
		tmpSet.Fields.forEach((pField) => { if ((pField.Group || '') === pOldKey) { pField.Group = tmpNew; } });
		tmpGroup.Key = tmpNew;
		this._touch(); this._repaintBody();
	}

	setNewCode(pOldCode, pValue)
	{
		const tmpField = this._field(pOldCode);
		if (!tmpField || !tmpField._New) { return; }
		const tmpNew = String(pValue == null ? '' : pValue).trim();
		if (this.state.Expanded[pOldCode]) { delete this.state.Expanded[pOldCode]; if (tmpNew) { this.state.Expanded[tmpNew] = true; } }
		if (!tmpField.ExternalID || (tmpField.ExternalID === pOldCode) || this._isProvisional(tmpField.ExternalID)) { tmpField.ExternalID = tmpNew; }
		tmpField.Code = tmpNew;
		this._touch(); this._repaintBody();
	}

	setNewValueCode(pCode, pOldValue, pNewValue)
	{
		const tmpField = this._field(pCode);
		if (!tmpField) { return; }
		const tmpEntry = tmpField.Values.find((pItem) => (pItem.Value === pOldValue));
		if (!tmpEntry || !tmpEntry._New) { return; }
		tmpEntry.Value = String(pNewValue == null ? '' : pNewValue).trim();
		this._touch(); this._repaintBody();
	}

	setDataType(pCode, pValue)
	{
		const tmpField = this._field(pCode);
		if (!tmpField || !tmpField._New) { return; } // changing a saved field's type would reinterpret stored values
		if (tmpField.Values.length) { return; }       // a value list forces enum; clear values to change type
		if (libAssetDescriptor.DATA_TYPES.indexOf(String(pValue)) > -1) { tmpField.DataType = String(pValue); }
		this._touch(); this._repaintBody();
	}

	setConstraint(pCode, pWhich, pValue)
	{
		const tmpField = this._field(pCode);
		if (!tmpField) { return; }
		if (pWhich === 'Required') { tmpField.Constraints.Required = !!pValue; this._touch(); this._repaintBody(); return; }
		if ([ 'Min', 'Max', 'MaxLength' ].indexOf(pWhich) < 0) { return; }
		const tmpNumber = (String(pValue).trim() === '') ? null : Number(pValue);
		tmpField.Constraints[pWhich] = Number.isFinite(tmpNumber) ? tmpNumber : null;
		this._touch(); this._syncStatus();
	}

	/**
	 * Blocking problems with in-session authored nodes: a blank or still-provisional identifier, or a
	 * duplicate key/code/value. Runs BEFORE _mergedManifest, because normalize turns a provisional code
	 * into a literal stored key that _problems would then wave through. Returns '' when clean.
	 *
	 * @return {String}
	 */
	_structuralProblems()
	{
		const tmpState = this.state;
		if (!tmpState.Descriptor) { return ''; }
		const tmpBad = (pValue) => (!String(pValue == null ? '' : pValue).trim() || this._isProvisional(pValue));
		const tmpProblems = [];
		const tmpSetKeys = {};
		(tmpState.Descriptor.MetadataSets || []).forEach((pSet) =>
		{
			const tmpSetName = pSet.Label || pSet.Key || 'a set';
			if (pSet._New && tmpBad(pSet.Key)) { tmpProblems.push('A new metadata set still needs a key.'); }
			if (pSet.Key && tmpSetKeys[pSet.Key]) { tmpProblems.push(`Two metadata sets share the key "${ pSet.Key }".`); }
			tmpSetKeys[pSet.Key] = true;
			const tmpGroupKeys = {};
			(pSet.Groups || []).forEach((pGroup) =>
			{
				if (pGroup._New && tmpBad(pGroup.Key)) { tmpProblems.push(`A new section in "${ tmpSetName }" still needs a key.`); }
				if (pGroup.Key && tmpGroupKeys[pGroup.Key]) { tmpProblems.push(`Two sections in "${ tmpSetName }" share the key "${ pGroup.Key }".`); }
				tmpGroupKeys[pGroup.Key] = true;
			});
			const tmpFieldCodes = {};
			(pSet.Fields || []).forEach((pField) =>
			{
				const tmpFieldName = pField.Label || pField.Code || 'a field';
				if (pField._New && tmpBad(pField.Code)) { tmpProblems.push(`A new field in "${ tmpSetName }" still needs a code.`); }
				if (pField.Code && tmpFieldCodes[pField.Code]) { tmpProblems.push(`Two fields in "${ tmpSetName }" share the code "${ pField.Code }".`); }
				tmpFieldCodes[pField.Code] = true;
				const tmpValueCodes = {};
				(pField.Values || []).forEach((pValue) =>
				{
					if (pValue._New && tmpBad(pValue.Value)) { tmpProblems.push(`A new coded value on "${ tmpFieldName }" still needs a code.`); }
					if (pValue.Value && tmpValueCodes[pValue.Value]) { tmpProblems.push(`Two coded values on "${ tmpFieldName }" share "${ pValue.Value }".`); }
					tmpValueCodes[pValue.Value] = true;
				});
			});
		});
		if (!tmpProblems.length) { return ''; }
		const tmpUnique = tmpProblems.filter((pItem, pIndex) => (tmpProblems.indexOf(pItem) === pIndex));
		return `Finish the new items before saving:\n  • ${ tmpUnique.join('\n  • ') }`;
	}

	// ========================================================= saving =========================================================

	/**
	 * The blob to store: everything that was there, with this screen's keys written over it.
	 *
	 * The overlay direction is the whole point. normalizeDescriptor returns only the six descriptor
	 * keys, so serializing IT would drop the legacy `Properties` array and anything else sharing the
	 * column. Raw first, descriptor second.
	 *
	 * @param {String} pStatus - 'draft' or 'published', always written explicitly
	 * @return {Object}
	 */
	_mergedManifest(pStatus)
	{
		const tmpState = this.state;
		const tmpDescriptor = libAssetDescriptor.normalizeDescriptor(tmpState.Descriptor);
		tmpDescriptor.Status = pStatus;
		return Object.assign({}, (tmpState.Raw && (typeof tmpState.Raw === 'object')) ? tmpState.Raw : {}, tmpDescriptor);
	}

	/**
	 * Refuse a write that cannot survive the round trip, using the guards the module already ships.
	 *
	 * @param {Object} pManifest
	 * @return {String} an empty string when it is safe to write
	 */
	_problems(pManifest)
	{
		const tmpBytes = libAssetDescriptor.serializedByteLength(pManifest);
		if (tmpBytes > MEDIUMTEXT_BYTES)
		{
			return `This shape is ${ tmpBytes.toLocaleString() } bytes and the column holds ${ MEDIUMTEXT_BYTES.toLocaleString() }. It would be truncated into unreadable JSON rather than refused, so it has not been saved.`;
		}
		// validateDescriptor returns a FLAT array of { Severity, Path, Message }. Errors block the write;
		// warnings are shown beside the save buttons instead, because they describe a descriptor that is
		// storable but will not do what its author expects.
		const tmpProblems = libAssetDescriptor.validateDescriptor(pManifest) || [];
		const tmpErrors = tmpProblems.filter((pProblem) => (pProblem.Severity === 'error'));
		if (tmpErrors.length)
		{
			return `This shape is not valid:\n  • ${ tmpErrors.map((pProblem) => `${ pProblem.Path }: ${ pProblem.Message }`).join('\n  • ') }`;
		}
		return '';
	}

	/**
	 * Non-blocking complaints about the shape as it currently stands.
	 *
	 * @return {Array<String>}
	 */
	_warnings()
	{
		const tmpState = this.state;
		if (!tmpState.Descriptor || !tmpState.Descriptor.MetadataSets.length) { return []; }
		const tmpProblems = libAssetDescriptor.validateDescriptor(this._mergedManifest(this._storedStatus())) || [];
		return tmpProblems.filter((pProblem) => (pProblem.Severity !== 'error')).map((pProblem) => `${ pProblem.Path }: ${ pProblem.Message }`);
	}

	saveDraftAsync() { return this._writeAsync('draft'); }
	publishAsync() { return this._writeAsync('published'); }

	/**
	 * Write the descriptor.
	 *
	 * A DRAFT row is written over: nothing renders it, so there is nothing to disturb. A PUBLISHED row
	 * is never mutated — it is serving every asset of this type at this moment — so an edit to one
	 * becomes a new version instead.
	 *
	 * @param {String} pStatus
	 */
	async _writeAsync(pStatus)
	{
		const tmpState = this.state;
		if (tmpState.Saving || !tmpState.Descriptor || !tmpState.IDAssetType) { return; }
		if (!tmpState.Descriptor.MetadataSets.length)
		{
			tmpState.Error = 'There is no metadata shape to save yet. Add a metadata set (or pull one in from the library on the Asset Types screen) first.';
			this.render();
			return;
		}

		const tmpStructural = this._structuralProblems();
		if (tmpStructural) { tmpState.Error = tmpStructural; tmpState.Message = ''; this.render(); return; }

		const tmpManifest = this._mergedManifest(pStatus);
		const tmpProblem = this._problems(tmpManifest);
		if (tmpProblem) { tmpState.Error = tmpProblem; tmpState.Message = ''; this.render(); return; }

		tmpState.Saving = true; tmpState.Error = ''; tmpState.Message = '';
		this.render();
		try
		{
			const tmpJSON = JSON.stringify(tmpManifest);
			// NO IDCustomer in this payload, ever. The server stamps it from the session and rejects a
			// value that disagrees with it.
			const tmpExistingStatus = this._storedStatus();
			let tmpVerb = '';
			if (tmpState.Row && (tmpExistingStatus !== 'published'))
			{
				const tmpResult = await this._put('AssetTypeManifest', { IDAssetTypeManifest: tmpState.Row.IDAssetTypeManifest, ManifestJSON: tmpJSON });
				if (tmpResult && tmpResult.Error) { throw new Error(tmpResult.Error); }
				tmpVerb = `version ${ tmpState.Row.Version } updated`;
			}
			else
			{
				const tmpVersion = tmpState.Versions.length ? (Math.max(...tmpState.Versions.map((pRow) => Number(pRow.Version) || 0)) + 1) : 1;
				const tmpResult = await this._post('AssetTypeManifest', { IDAssetType: tmpState.IDAssetType, Version: tmpVersion, ManifestJSON: tmpJSON });
				if (tmpResult && tmpResult.Error) { throw new Error(tmpResult.Error); }
				tmpVerb = `saved as version ${ tmpVersion }`;
			}

			// The dashboard caches the manifest fetch AND the normalized descriptor per (type, version).
			// Without this the rest of the session renders the shape that was there before this save.
			const tmpDescriptorProvider = this.pict.providers.AssetDescriptor;
			if (tmpDescriptorProvider && (typeof tmpDescriptorProvider.invalidate === 'function')) { tmpDescriptorProvider.invalidate(tmpState.IDAssetType); }

			await this.selectTypeAsync(tmpState.IDAssetType);
			tmpState.Message = (pStatus === 'published')
				? `Published — ${ tmpVerb }. Every ${ this.accessor.singularLabel.toLowerCase() } of this type now reads this shape.`
				: `Draft ${ tmpVerb }. Nothing on the dashboard changes until you publish it.`;
		}
		catch (pError)
		{
			tmpState.Error = `Save failed: ${ (pError && pError.message) || pError }`;
		}
		finally
		{
			// try/FINALLY. The sibling screen resets this only on the failure path, so its buttons never
			// come back after the first successful save.
			tmpState.Saving = false;
			this.render();
		}
	}

	/** The Status of the row as it is STORED, not as it has been edited on screen. */
	_storedStatus()
	{
		const tmpRaw = this.state.Raw;
		const tmpStatus = (tmpRaw && tmpRaw.Status) ? String(tmpRaw.Status) : '';
		return (tmpStatus === 'published') ? 'published' : 'draft';
	}

	openDashboard() { window.location.hash = '#/AssetDashboard'; }

	// ========================================================= render =========================================================

	render()
	{
		if (!this.pict.views.AssetTypeDescriptor) { return; }
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpCA.getElement(DETAIL_MOUNT).length) { return; }
		const tmpState = this.state;

		if (tmpState.Loading && !tmpState.Loaded) { tmpCA.assignContent(DETAIL_MOUNT, '<div class="atd-empty"><i class="fas fa-circle-notch fa-spin"></i> Loading…</div>'); return; }
		if (tmpState.Loaded && !tmpState.Types.length)
		{
			tmpCA.assignContent(DETAIL_MOUNT, '<div class="atd-empty">There are no asset types yet. Add one from the library on the Asset Types screen first.</div>');
			return;
		}

		const tmpTypeOptions = tmpState.Types.map((pType) =>
			`<option value="${ Number(pType.IDAssetType) }"${ (Number(pType.IDAssetType) === Number(tmpState.IDAssetType)) ? ' selected' : '' }>${ this._esc(pType.Name || `Type ${ pType.IDAssetType }`) }</option>`).join('');

		const tmpSets = (tmpState.Descriptor && tmpState.Descriptor.MetadataSets) ? tmpState.Descriptor.MetadataSets : [];
		const tmpSetPicker = (tmpSets.length > 1)
			? `<span class="atd-bar-label">Set</span><select class="atd-select" onchange="_Pict.providers.AssetTypeDescriptor.setSetKey(this.value)">${
				tmpSets.map((pSet) => `<option value="${ this._esc(pSet.Key) }"${ (pSet.Key === tmpState.SetKey) ? ' selected' : '' }>${ this._esc(pSet.Label || pSet.Key) }</option>`).join('') }</select>`
			: '';

		const tmpHTML = `
			${ tmpState.Error ? `<div class="atd-error">${ this._esc(tmpState.Error) }</div>` : '' }
			${ tmpState.Message ? `<div class="atd-msg">${ this._esc(tmpState.Message) }</div>` : '' }
			<div class="atd-bar">
				${ this._fixedIDAssetType ? '' : '<span class="atd-bar-label">Asset type</span>' }
				${ this._fixedIDAssetType ? '' : `<select class="atd-select" onchange="_Pict.providers.AssetTypeDescriptor.selectTypeAsync(this.value)">${ tmpTypeOptions }</select>` }
				${ tmpSetPicker }${ this.canEdit() ? '<button class="atd-add" title="Add a metadata set" onclick="_Pict.providers.AssetTypeDescriptor.addMetadataSet()"><i class="fas fa-plus"></i> Set</button>' : '' }
				<span class="atd-spacer"></span>
				<span id="ATD-Status">${ this._statusHTML() }</span>
			</div>
			${ this._identityHTML() }
			<div class="atd-area-head">Fields <span class="atd-hint">— the eye shows a value on the asset detail panel; the chart makes it a map metric. Field codes are fixed: stored values bind to them.</span></div>
			<div class="atd-bar" style="border:0;padding:0;margin:0 0 0.5rem;">
				<input class="atd-input atd-input--md" placeholder="Filter by name or code…" value="${ this._esc(tmpState.Filter) }" oninput="_Pict.providers.AssetTypeDescriptor.setFilter(this.value)">
				${ this._defaultMetricHTML() }
			</div>
			<div id="ATD-Body" class="atd-body">${ this._bodyHTML() }</div>
			${ this._saveBarHTML() }`;
		tmpCA.assignContent(DETAIL_MOUNT, tmpHTML);
	}

	/** Repaint only the field list, keeping the scroll position — the list runs to 149 rows. */
	_repaintBody()
	{
		const tmpCA = this.pict.ContentAssignment;
		const tmpMount = tmpCA.getElement(BODY_MOUNT);
		if (!tmpMount || !tmpMount.length) { this.render(); return; }
		let tmpScroll = 0;
		try { tmpScroll = tmpMount[0].scrollTop || 0; } catch (pError) { /* not a live DOM node */ }
		tmpCA.assignContent(BODY_MOUNT, this._bodyHTML());
		try { const tmpAfter = tmpCA.getElement(BODY_MOUNT); if (tmpAfter && tmpAfter.length) { tmpAfter[0].scrollTop = tmpScroll; } } catch (pError) { /* ditto */ }
		this._syncStatus();
	}

	/** Repaint the status pills alone — safe to call from a text input's oninput. */
	_syncStatus()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpCA.getElement(STATUS_MOUNT).length) { return; }
		tmpCA.assignContent(STATUS_MOUNT, this._statusHTML());
	}

	_statusHTML()
	{
		const tmpState = this.state;
		if (!tmpState.Row && !tmpState.Descriptor) { return ''; }
		const tmpStored = this._storedStatus();
		const tmpParts = [];
		tmpParts.push(`<span class="atd-pill atd-pill--${ tmpStored }">${ tmpStored }</span>`);
		if (tmpState.Row) { tmpParts.push(`<span class="atd-pill">v${ tmpState.Row.Version }</span>`); }
		if (tmpState.Dirty) { tmpParts.push('<span class="atd-pill atd-pill--dirty">unsaved</span>'); }
		return tmpParts.join(' ');
	}

	_identityHTML()
	{
		const tmpState = this.state;
		if (!tmpState.Descriptor) { return ''; }
		const tmpLabel = tmpState.Descriptor.AssetType.Label;
		return `<div class="atd-ident">
			<label class="atd-fieldwrap"><span class="atd-fieldwrap-label">One is called</span><input class="atd-input atd-input--md" value="${ this._esc(tmpLabel.Singular) }" oninput="_Pict.providers.AssetTypeDescriptor.setAssetLabel('Singular', this.value)"></label>
			<label class="atd-fieldwrap"><span class="atd-fieldwrap-label">Many are called</span><input class="atd-input atd-input--md" value="${ this._esc(tmpLabel.Plural) }" oninput="_Pict.providers.AssetTypeDescriptor.setAssetLabel('Plural', this.value)"></label>
		</div>`;
	}

	_defaultMetricHTML()
	{
		const tmpAccessor = this.accessor;
		const tmpGroups = tmpAccessor.metrics();
		if (!tmpGroups.length) { return '<span class="atd-hint">No map metrics chosen yet — turn on a chart icon to offer one.</span>'; }
		const tmpCurrent = this.state.Descriptor.Dashboard.DefaultMetric || '';
		const tmpOptions = tmpGroups.map((pGroup) => `<optgroup label="${ this._esc(pGroup.Group) }">${
			pGroup.Fields.map((pField) => `<option value="${ this._esc(pField.Code) }"${ (pField.Code === tmpCurrent) ? ' selected' : '' }>${ this._esc(pField.Label) }</option>`).join('') }</optgroup>`).join('');
		return `<span class="atd-bar-label">Map opens on</span><select class="atd-select" onchange="_Pict.providers.AssetTypeDescriptor.setDefaultMetric(this.value)"><option value="">Nothing</option>${ tmpOptions }</select>`;
	}

	_bodyHTML()
	{
		const tmpState = this.state;
		if (tmpState.Loading) { return '<div class="atd-empty"><i class="fas fa-circle-notch fa-spin"></i> Loading…</div>'; }
		const tmpCanEdit = this.canEdit();
		if (!tmpState.Descriptor || !tmpState.Descriptor.MetadataSets.length)
		{
			const tmpAddSet = tmpCanEdit ? `<div class="atd-body-actions"><button class="atd-add" onclick="_Pict.providers.AssetTypeDescriptor.addMetadataSet()"><i class="fas fa-plus"></i> Add a metadata set</button></div>` : '';
			return `<div class="atd-empty">This asset type has no metadata shape yet.<br>Add one from scratch, or pull one in from the library on the Asset Types screen.</div>${ tmpAddSet }`;
		}
		const tmpGroups = this.accessor.groups();
		// accessor.groups() drops zero-field groups, so union in the set's declared-but-empty sections
		// (a freshly added section has no fields yet) so they can still be edited.
		const tmpSet = this.set;
		if (tmpSet)
		{
			tmpSet.Groups.slice().sort((pLeft, pRight) => (pLeft.Ordinal - pRight.Ordinal)).forEach((pGroup) =>
			{
				if (!tmpGroups.some((pItem) => (pItem.Key === pGroup.Key))) { tmpGroups.push(Object.assign({}, pGroup, { Fields: [] })); }
			});
			tmpGroups.sort((pLeft, pRight) => (pLeft.Ordinal - pRight.Ordinal));
		}
		const tmpAddSection = tmpCanEdit ? `<div class="atd-body-actions"><button class="atd-add" onclick="_Pict.providers.AssetTypeDescriptor.addGroup()"><i class="fas fa-plus"></i> Add section</button></div>` : '';
		// A brand-new, in-session set needs a KEY before it can be saved (its provisional key is blocked by the
		// save guard). Offer the key + optional label + remove here; it disappears once saved (reload strips _New).
		const tmpSetEditor = (tmpCanEdit && tmpSet && tmpSet._New) ? this._newSetEditorHTML(tmpSet) : '';
		if (!tmpGroups.length) { return tmpSetEditor + `<div class="atd-empty">This metadata set has no sections yet.</div>${ tmpAddSection }`; }
		const tmpHTML = tmpGroups.map((pGroup, pIndex) => this._groupHTML(pGroup, pIndex, tmpGroups.length)).join('');
		return tmpSetEditor + (tmpHTML || '<div class="atd-empty">Nothing matches that filter.</div>') + tmpAddSection;
	}

	/** Inline key/label/remove editor shown for a freshly-added (unsaved) metadata set. */
	_newSetEditorHTML(pSet)
	{
		const tmpKey = this._js(pSet.Key);
		const tmpLabelVal = (pSet.Label && !this._isProvisional(pSet.Label)) ? pSet.Label : '';
		return `<div class="atd-newset">
			<span class="atd-newset-l">New set — give it a key before saving</span>
			<input class="atd-input atd-input--md atd-code--edit" placeholder="set key (e.g. CulvertInspection)" value="${ this._esc(this._isProvisional(pSet.Key) ? '' : pSet.Key) }" onchange="_Pict.providers.AssetTypeDescriptor.setNewSetKey('${ tmpKey }', this.value)">
			<input class="atd-input atd-input--md" placeholder="label (optional)" value="${ this._esc(tmpLabelVal) }" oninput="_Pict.providers.AssetTypeDescriptor.setSetLabel('${ tmpKey }', this.value)">
			<button class="atd-del" title="Remove this set" onclick="_Pict.providers.AssetTypeDescriptor.removeMetadataSet('${ tmpKey }')"><i class="fas fa-trash"></i></button>
		</div>`;
	}

	_matches(pField)
	{
		const tmpFilter = String(this.state.Filter || '').trim().toLowerCase();
		if (!tmpFilter) { return true; }
		return ((String(pField.Label || '').toLowerCase().indexOf(tmpFilter) > -1) || (String(pField.Code || '').toLowerCase().indexOf(tmpFilter) > -1));
	}

	/** <option> list for the field DataType select, from the module's single source of truth. */
	_dataTypeOptions(pCurrent)
	{
		const tmpLabels = { enum: 'Coded list', string: 'Short text', text: 'Long text', number: 'Number', integer: 'Whole number', date: 'Date', boolean: 'Yes / No' };
		return libAssetDescriptor.DATA_TYPES.map((pType) => `<option value="${ pType }"${ (pType === pCurrent) ? ' selected' : '' }>${ tmpLabels[pType] || pType }</option>`).join('');
	}

	_groupHTML(pGroup, pIndex, pCount)
	{
		const tmpFields = pGroup.Fields.filter((pField) => this._matches(pField));
		const tmpFiltering = (String(this.state.Filter || '').trim().length > 0);
		// Hide a section with no matches WHILE filtering; otherwise keep empty sections so they can be edited.
		if (!tmpFields.length && tmpFiltering) { return ''; }
		const tmpKey = this._js(pGroup.Key);
		const tmpShown = pGroup.Fields.filter((pField) => pField.Visible).length;
		const tmpSet = this.set;
		const tmpGroupObj = tmpSet ? tmpSet.Groups.find((pItem) => (pItem.Key === pGroup.Key)) : null;
		const tmpIsNew = !!(tmpGroupObj && tmpGroupObj._New);
		const tmpCanEdit = this.canEdit();
		const tmpKeyCell = tmpIsNew
			? `<input class="atd-grp-key atd-grp-key--edit" placeholder="section key" value="${ this._esc(pGroup.Key) }" onchange="_Pict.providers.AssetTypeDescriptor.setNewGroupKey('${ tmpKey }', this.value)">`
			: `<span class="atd-grp-key" title="Section key — fixed">${ this._esc(pGroup.Key || '—') }</span>`;
		// Field add / section remove only for real declared sections (not the synthesized "Other" bucket).
		const tmpAddRemove = (tmpCanEdit && tmpGroupObj)
			? `<button class="atd-add atd-add--sm" title="Add a field to this section" onclick="_Pict.providers.AssetTypeDescriptor.addField('${ tmpKey }')"><i class="fas fa-plus"></i> Field</button><button class="atd-del" title="Remove this section" onclick="_Pict.providers.AssetTypeDescriptor.removeGroup('${ tmpKey }')"><i class="fas fa-trash"></i></button>`
			: '';
		// The synthesized "Other"/ungrouped bucket is not a declared group: its label can't be renamed and it
		// can't be reordered (both setters key off declared Groups), so render those controls inert-free.
		const tmpLabelCell = tmpGroupObj
			? `<input class="atd-grp-label" value="${ this._esc(this._cleanLabel(tmpGroupObj)) }" title="Rename this section" oninput="_Pict.providers.AssetTypeDescriptor.setGroupLabel('${ tmpKey }', this.value)">`
			: `<span class="atd-grp-label atd-grp-label--ro" title="Fields with no section">${ this._esc(pGroup.Label || 'Other') }</span>`;
		const tmpMoveCells = tmpGroupObj
			? `<button class="atd-mv" title="Move section up" ${ (pIndex === 0) ? 'disabled' : '' } onclick="_Pict.providers.AssetTypeDescriptor.moveGroup('${ tmpKey }', -1)"><i class="fas fa-chevron-up"></i></button><button class="atd-mv" title="Move section down" ${ (pIndex >= (pCount - 1)) ? 'disabled' : '' } onclick="_Pict.providers.AssetTypeDescriptor.moveGroup('${ tmpKey }', 1)"><i class="fas fa-chevron-down"></i></button>`
			: '';
		return `<div class="atd-grp">
			<div class="atd-grp-head">
				${ tmpKeyCell }
				${ tmpLabelCell }
				<span class="atd-grp-count">${ tmpShown } of ${ pGroup.Fields.length } shown</span>
				<button class="atd-grp-bulk" onclick="_Pict.providers.AssetTypeDescriptor.setGroupVisible('${ tmpKey }', true)">Show all</button>
				<button class="atd-grp-bulk" onclick="_Pict.providers.AssetTypeDescriptor.setGroupVisible('${ tmpKey }', false)">Hide all</button>
				${ tmpMoveCells }
				${ tmpAddRemove }
			</div>
			${ tmpFields.map((pField) => this._rowHTML(pField)).join('') }
		</div>`;
	}

	_rowHTML(pField)
	{
		const tmpCode = this._js(pField.Code);
		const tmpExpanded = !!this.state.Expanded[pField.Code];
		const tmpIsNew = !!pField._New;
		const tmpCanEdit = this.canEdit();
		const tmpCodeCell = tmpIsNew
			? `<input class="atd-code atd-code--edit" placeholder="field code" value="${ this._esc(pField.Code) }" onchange="_Pict.providers.AssetTypeDescriptor.setNewCode('${ tmpCode }', this.value)">`
			: `<span class="atd-code" title="Field code ${ this._esc(pField.Code) } — fixed, because stored values bind to it">${ this._esc(pField.Code) }</span>`;
		const tmpTypeCell = tmpIsNew
			? `<select class="atd-type-edit"${ pField.Values.length ? ' disabled title="Clear the coded values to change the type"' : '' } onchange="_Pict.providers.AssetTypeDescriptor.setDataType('${ tmpCode }', this.value)">${ this._dataTypeOptions(pField.DataType) }</select>`
			: `<span class="atd-type">${ this._esc(pField.DataType) }</span>`;
		const tmpRemove = tmpCanEdit ? `<button class="atd-del" title="Remove this field" onclick="_Pict.providers.AssetTypeDescriptor.removeField('${ tmpCode }')"><i class="fas fa-trash"></i></button>` : '';
		return `<div class="atd-row${ pField.Visible ? '' : ' is-hidden' }">
			<span class="atd-toggles">
				<button class="atd-tg${ pField.Visible ? ' is-on-eye' : '' }" title="${ pField.Visible ? 'Shown on the detail panel — click to hide' : 'Hidden — click to show' }" onclick="_Pict.providers.AssetTypeDescriptor.toggleVisible('${ tmpCode }')"><i class="fas fa-${ pField.Visible ? 'eye' : 'eye-slash' }"></i></button>
				<button class="atd-tg${ pField.Metric ? ' is-on' : '' }" title="${ pField.Metric ? 'The map can colour by this — click to stop' : 'Make this a map metric' }" onclick="_Pict.providers.AssetTypeDescriptor.toggleMetric('${ tmpCode }')"><i class="fas fa-chart-simple"></i></button>
			</span>
			${ tmpCodeCell }
			<input class="atd-label" value="${ this._esc(this._cleanLabel(pField)) }" oninput="_Pict.providers.AssetTypeDescriptor.setLabel('${ tmpCode }', this.value)">
			<input class="atd-units" placeholder="units" value="${ this._esc(pField.Units || '') }" oninput="_Pict.providers.AssetTypeDescriptor.setUnits('${ tmpCode }', this.value)">
			${ tmpTypeCell }
			<span class="atd-rowacts">
				<button class="atd-mv" title="Move up" onclick="_Pict.providers.AssetTypeDescriptor.moveField('${ tmpCode }', -1)"><i class="fas fa-chevron-up"></i></button>
				<button class="atd-mv" title="Move down" onclick="_Pict.providers.AssetTypeDescriptor.moveField('${ tmpCode }', 1)"><i class="fas fa-chevron-down"></i></button>
				<button class="atd-tg${ tmpExpanded ? ' is-on' : '' }" title="Help text, formatting and value names" onclick="_Pict.providers.AssetTypeDescriptor.toggleExpand('${ tmpCode }')"><i class="fas fa-gear"></i></button>
				${ tmpRemove }
			</span>
		</div>${ tmpExpanded ? this._advancedHTML(pField) : '' }`;
	}

	_advancedHTML(pField)
	{
		const tmpCode = this._js(pField.Code);
		const tmpNumeric = ((pField.DataType === 'number') || (pField.DataType === 'integer'));
		const tmpString = ((pField.DataType === 'string') || (pField.DataType === 'text'));
		const tmpCanEdit = this.canEdit();
		const tmpDisplayOptions = libAssetDescriptor.VALUE_DISPLAYS.map((pDisplay) =>
			`<option value="${ pDisplay }"${ (pField.Format.ValueDisplay === pDisplay) ? ' selected' : '' }>${ ({ 'label-only': 'Meaning only', 'code-and-label': 'Code and meaning', 'raw': 'Raw value' })[pDisplay] || pDisplay }</option>`).join('');
		// Show the values editor whenever the field is (or is becoming) an enum, so a new enum can get its first value.
		const tmpShowValues = ((pField.DataType === 'enum') || (pField.Values.length > 0));
		const tmpOrdered = pField.Values.slice().sort((pLeft, pRight) => (pLeft.Ordinal - pRight.Ordinal));
		const tmpValueRows = tmpOrdered.map((pValue, pValueIndex) =>
		{
			const tmpValueJS = this._js(pValue.Value);
			const tmpValueCode = (tmpCanEdit && pValue._New)
				? `<input class="atd-val-code atd-val-code--edit" placeholder="code" value="${ this._esc(pValue.Value) }" onchange="_Pict.providers.AssetTypeDescriptor.setNewValueCode('${ tmpCode }', '${ tmpValueJS }', this.value)">`
				: `<span class="atd-val-code">${ this._esc(pValue.Value) }</span>`;
			const tmpValueActs = tmpCanEdit
				? `<button class="atd-val-mv" title="Move up" ${ (pValueIndex === 0) ? 'disabled' : '' } onclick="_Pict.providers.AssetTypeDescriptor.moveValue('${ tmpCode }', '${ tmpValueJS }', -1)"><i class="fas fa-chevron-up"></i></button><button class="atd-val-mv" title="Move down" ${ (pValueIndex >= (tmpOrdered.length - 1)) ? 'disabled' : '' } onclick="_Pict.providers.AssetTypeDescriptor.moveValue('${ tmpCode }', '${ tmpValueJS }', 1)"><i class="fas fa-chevron-down"></i></button><button class="atd-val-del" title="Remove this value" onclick="_Pict.providers.AssetTypeDescriptor.removeValue('${ tmpCode }', '${ tmpValueJS }')"><i class="fas fa-xmark"></i></button>`
				: '';
			return `<div class="atd-val">${ tmpValueCode }<input class="atd-val-label" value="${ this._esc(this._cleanLabel(pValue)) }" oninput="_Pict.providers.AssetTypeDescriptor.setValueLabel('${ tmpCode }', '${ tmpValueJS }', this.value)">${ tmpValueActs }</div>`;
		}).join('');
		const tmpValues = tmpShowValues
			? `<div class="atd-vals"><div class="atd-vals-h">What each code means</div>${ tmpValueRows }${ tmpCanEdit ? `<button class="atd-val-add" onclick="_Pict.providers.AssetTypeDescriptor.addValue('${ tmpCode }')"><i class="fas fa-plus"></i> Add value</button>` : '' }</div>`
			: '';
		const tmpRequired = `<label class="atd-fieldwrap atd-fieldwrap--chk"><span class="atd-fieldwrap-label">Required</span><input type="checkbox" ${ pField.Constraints.Required ? 'checked' : '' } onchange="_Pict.providers.AssetTypeDescriptor.setConstraint('${ tmpCode }', 'Required', this.checked)"></label>`;
		const tmpMaxLength = tmpString ? `<label class="atd-fieldwrap"><span class="atd-fieldwrap-label">Max length</span><input class="atd-input atd-input--sm" type="number" min="0" value="${ (pField.Constraints.MaxLength == null) ? '' : pField.Constraints.MaxLength }" onchange="_Pict.providers.AssetTypeDescriptor.setConstraint('${ tmpCode }', 'MaxLength', this.value)"></label>` : '';
		const tmpMinMax = tmpNumeric ? `<label class="atd-fieldwrap"><span class="atd-fieldwrap-label">Min</span><input class="atd-input atd-input--sm" type="number" value="${ (pField.Constraints.Min == null) ? '' : pField.Constraints.Min }" onchange="_Pict.providers.AssetTypeDescriptor.setConstraint('${ tmpCode }', 'Min', this.value)"></label><label class="atd-fieldwrap"><span class="atd-fieldwrap-label">Max</span><input class="atd-input atd-input--sm" type="number" value="${ (pField.Constraints.Max == null) ? '' : pField.Constraints.Max }" onchange="_Pict.providers.AssetTypeDescriptor.setConstraint('${ tmpCode }', 'Max', this.value)"></label>` : '';
		return `<div class="atd-adv">
			<div class="atd-adv-grid">
				${ pField.Values.length ? `<label class="atd-fieldwrap"><span class="atd-fieldwrap-label">Show coded values as</span><select class="atd-select" onchange="_Pict.providers.AssetTypeDescriptor.setValueDisplay('${ tmpCode }', this.value)">${ tmpDisplayOptions }</select></label>` : '' }
				${ tmpNumeric ? `<label class="atd-fieldwrap"><span class="atd-fieldwrap-label">Decimal places</span><input class="atd-input atd-input--sm" type="number" min="0" max="8" value="${ (pField.Format.Decimals == null) ? '' : pField.Format.Decimals }" oninput="_Pict.providers.AssetTypeDescriptor.setDecimals('${ tmpCode }', this.value)"></label>` : '' }
				${ tmpRequired }
				${ tmpMaxLength }
				${ tmpMinMax }
			</div>
			<textarea class="atd-help" placeholder="Help text — shown as the tooltip wherever this value appears." oninput="_Pict.providers.AssetTypeDescriptor.setHelp('${ tmpCode }', this.value)">${ this._esc(pField.Help) }</textarea>
			${ tmpValues }
		</div>`;
	}

	_saveBarHTML()
	{
		const tmpState = this.state;
		if (!tmpState.Descriptor || !tmpState.Descriptor.MetadataSets.length) { return ''; }
		const tmpBytes = libAssetDescriptor.serializedByteLength(this._mergedManifest(this._storedStatus()));
		const tmpOver = (tmpBytes > TEXT_BYTES);
		const tmpSizeNote = tmpOver
			? `${ tmpBytes.toLocaleString() } bytes — over the old TEXT limit, so this column must already be MEDIUMTEXT`
			: `${ tmpBytes.toLocaleString() } bytes`;
		const tmpDisabled = tmpState.Saving ? 'disabled' : '';
		const tmpCanEdit = this.canEdit();
		if (!tmpCanEdit) { return '<div class="atd-warn">You do not have permission to change asset metadata shapes.</div>'; }
		const tmpWarnings = this._warnings();
		return `<div class="atd-savebar">
			<button class="atd-save atd-save--primary" ${ tmpDisabled } onclick="_Pict.providers.AssetTypeDescriptor.publishAsync()">${ tmpState.Saving ? 'Saving…' : 'Publish' }</button>
			<button class="atd-save" ${ tmpDisabled } onclick="_Pict.providers.AssetTypeDescriptor.saveDraftAsync()">Save as draft</button>
			<button class="atd-save" onclick="_Pict.providers.AssetTypeDescriptor.openDashboard()">Open the dashboard</button>
			<span class="atd-size${ tmpOver ? ' is-over' : '' }">${ this._esc(tmpSizeNote) }</span>
		</div>
		${ (this._storedStatus() === 'published') ? '<div class="atd-warn">This version is published and is what every asset of this type reads right now. Publishing again writes a new version rather than changing it underneath them.</div>' : '' }
		${ tmpWarnings.length ? `<div class="atd-warn">${ tmpWarnings.map((pWarning) => this._esc(pWarning)).join('<br>') }</div>` : '' }`;
	}
}

module.exports = AssetTypeDescriptorProvider;
module.exports.default_configuration =
{
	ProviderIdentifier: 'AssetTypeDescriptor',
	AutoInitialize: true,
	AutoInitializeOrdinal: 232,
};
